// Code generated by apic; DO NOT EDIT.
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, type ChangeEvent } from "react";
import type { RestEndpointMeta, SchemaMeta } from "./types";
import type { ApicClient } from "../react/client";
import { EndpointCard } from "./EndpointCard";

/**
 * EndpointList renders a filtered list of REST endpoints as EndpointCard
 * components.  A labeled search/filter input at the top narrows the list to
 * endpoints whose method, path, or description contains the query string
 * (case-insensitive).
 *
 * Props:
 *   endpoints - the list of RestEndpointMeta to display.
 *   schemas   - optional map of schema name to SchemaMeta passed through to each card.
 *   baseUrl   - optional base URL prefix forwarded to each EndpointCard.
 *   client    - optional shared ApicClient forwarded to each EndpointCard; when
 *               absent each card creates its own via useExplorerClient(baseUrl).
 *   className - extra CSS class appended to the root element.
 *
 * Each card uses a stable React key derived from the endpoint's HTTP method and
 * path (e.g. "GET /v1/ping").
 *
 * No web-global dependencies at module load time (RN-safe).
 */
export function EndpointList(props: {
  endpoints: RestEndpointMeta[];
  schemas?: Record<string, SchemaMeta>;
  baseUrl?: string;
  client?: ApicClient;
  className?: string;
}): JSX.Element {
  const { endpoints, schemas, baseUrl, client, className } = props;
  const root = ["apic-ui-endpoint-list", className].filter(Boolean).join(" ");

  const [filterQuery, setFilterQuery] = useState<string>("");

  // Filter predicate: case-insensitive substring match on method, path, or description.
  const query = filterQuery.trim().toLowerCase();
  const visible = query === ""
    ? endpoints
    : endpoints.filter((ep) =>
        ep.method.toLowerCase().includes(query) ||
        ep.path.toLowerCase().includes(query) ||
        ep.description.toLowerCase().includes(query),
      );

  return (
    <div className={root}>
      {/* Search/filter input */}
      <div className="apic-ui-endpoint-list-filter">
        <label
          className="apic-ui-endpoint-list-filter-label"
          htmlFor="apic-ui-endpoint-list-search"
        >
          {"Filter endpoints"}
        </label>
        <input
          id="apic-ui-endpoint-list-search"
          type="search"
          className="apic-ui-endpoint-list-filter-input"
          placeholder="method, path, or description"
          value={filterQuery}
          onChange={(e: ChangeEvent<HTMLInputElement>) => { setFilterQuery(e.target.value); }}
          aria-label="Filter endpoints by method, path, or description"
        />
        {visible.length !== endpoints.length && (
          <span className="apic-ui-endpoint-list-filter-count">
            {visible.length + " / " + endpoints.length}
          </span>
        )}
      </div>

      {/* Endpoint cards */}
      {visible.map((ep) => (
        <div key={ep.method + " " + ep.path} className="apic-ui-endpoint-list-item">
          <EndpointCard
            endpoint={ep}
            {...(schemas !== undefined ? { schemas } : {})}
            {...(baseUrl !== undefined ? { baseUrl } : {})}
            {...(client !== undefined ? { client } : {})}
          />
        </div>
      ))}

      {/* Empty state when filter matches nothing */}
      {visible.length === 0 && query !== "" && (
        <div className="apic-ui-endpoint-list-empty">
          {"No endpoints match: " + filterQuery}
        </div>
      )}
    </div>
  );
}
